import React, {FC} from 'react';
import styled, {useTheme} from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {LoadedMessageModel} from '../../../libs/shared/core/src/models/api/messageModel';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import successStartGuide2 from '../../assets/images/successStartGuide2.png';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../helpers/imageThemeHelpers';
import {ConversationAiStatusesEnum} from '../../models/api/conversationAiMarkModel';
import {TagModel} from '../../models/api/tagModel';
import {AiHarvestingMessageView} from './aiHarvestingMessageView';

/*
 * Props.
 */

export interface AiHarvestingCtaProps {
  isLoading: boolean;
  hasError?: boolean;
  reviewStatus?: ConversationAiStatusesEnum;
  isHarvestingDone: boolean;
  message: LoadedMessageModel;
  onStatusChangeClick: (status: ConversationAiStatusesEnum) => void;
  tag: TagModel | undefined;
  canGoToNextTag: boolean;
  onNextTagClick: () => void;
  onFinishClick: () => void;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 16px;
  margin-top: 24px;
`;

const StyledQuestionWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledQuestionTextDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  display: flex;
  align-items: stretch;
`;

const StyledButtonGroupDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

// Center everything
const StyledDoneWrapperDiv = styled.div`
  flex: 1 1 auto;
  ${center()};
  max-width: 310px;
  margin: 0 auto;
`;

const StyledDoneImage = styled.img`
  margin-bottom: 24px;
`;

const StyledSubtitleDiv = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledTextDiv = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade80};
  margin-top: 8px;
  text-align: center;
  line-height: 22px;
`;

const StyledErrorBannerDiv = styled.div`
  display: flex;
  align-items: center;

  height: 34px;

  background-color: ${(p) => p.theme.palette.red.shade20};
  padding: 9px 9px 9px 12px;
  border-radius: 8px;
  gap: 8px;
`;

const StyledErrorTextDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledDoneButtonWrapperDiv = styled.div`
  margin-top: 24px;
`;

/*
 * Component.
 */

export const AiHarvestingCta: FC<AiHarvestingCtaProps> = ({
  isLoading,
  hasError,
  reviewStatus,
  message,
  isHarvestingDone,
  onStatusChangeClick,
  tag,
  onNextTagClick,
  canGoToNextTag,
  onFinishClick,
}) => {
  const theme = useTheme();
  const searchNoResultsAnywhere = findThemedImageInfo(theme, ThemedImageNamesEnum.SEARCH_NO_RESULTS_ANYWHERE);

  if (isLoading || !tag) {
    return renderLoadingState();
  }

  if (isHarvestingDone) {
    return (
      <StyledDoneWrapperDiv>
        <StyledDoneImage src={successStartGuide2} width={160} height={140} alt="" />
        <StyledSubtitleDiv>
          <Trans id="ai-harvesting-modal-done-title">Training completed for this tag</Trans>
        </StyledSubtitleDiv>
        <StyledTextDiv>
          <Trans id="ai-harvesting-modal-done-body">
            Auto tagging is now turned on. You can turn it off anytime in settings.
          </Trans>
        </StyledTextDiv>
      </StyledDoneWrapperDiv>
    );
  }

  if (!message) {
    return (
      <StyledDoneWrapperDiv>
        <StyledDoneImage
          src={searchNoResultsAnywhere.src}
          width={searchNoResultsAnywhere.width}
          height={searchNoResultsAnywhere.height}
          alt=""
        />
        <StyledSubtitleDiv>
          <Trans id="ai-harvesting-modal-waiting-for-more-title">No sample messages found yet</Trans>
        </StyledSubtitleDiv>
        <StyledTextDiv>
          <Trans id="ai-harvesting-modal-waiting-for-more-body">
            We&apos;ll email you when AI is done collecting more samples.
          </Trans>
        </StyledTextDiv>
        <StyledDoneButtonWrapperDiv>
          {canGoToNextTag ? (
            <Button
              size={VisualSizesEnum.MEDIUM}
              variant={ButtonVariantsEnum.PRIMARY}
              onClick={onNextTagClick}
            >
              <Trans id="ai-harvesting-modal-waiting-for-more-nexttag-cta">Next tag</Trans>
            </Button>
          ) : (
            <Button
              size={VisualSizesEnum.MEDIUM}
              variant={ButtonVariantsEnum.PRIMARY}
              onClick={onFinishClick}
            >
              <Trans id="ai-harvesting-modal-waiting-for-more-cancel-cta">OK</Trans>
            </Button>
          )}
        </StyledDoneButtonWrapperDiv>
      </StyledDoneWrapperDiv>
    );
  }

  return (
    <StyledWrapperDiv>
      <StyledQuestionWrapperDiv>
        <StyledQuestionTextDiv>
          <Trans id="ai-harvesting-modal-question">Is this message related to the tag?</Trans>
        </StyledQuestionTextDiv>
        <StyledButtonGroupDiv>{renderButtonGroup(reviewStatus, onStatusChangeClick)}</StyledButtonGroupDiv>
      </StyledQuestionWrapperDiv>
      {hasError && (
        <StyledErrorBannerDiv>
          <Icon size={16} name="warningActivity" color={theme.palette.red.shade50} />
          <StyledErrorTextDiv>
            <Trans id="ai-harvesting-modal-error">
              Response was not received because an error has occurred. Please try again.
            </Trans>
          </StyledErrorTextDiv>
        </StyledErrorBannerDiv>
      )}
      <AiHarvestingMessageView message={message} />
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderButtonGroup(
  reviewStatus: ConversationAiStatusesEnum | undefined,
  onStatusChangeClick: (status: ConversationAiStatusesEnum) => void,
) {
  if (reviewStatus && reviewStatus === ConversationAiStatusesEnum.DISCARDED) {
    return (
      <Button size={VisualSizesEnum.MEDIUM} isDisabled>
        <Trans id="ai-harvesting-modal-mutated-no">Skip</Trans>
      </Button>
    );
  }

  if (reviewStatus && reviewStatus === ConversationAiStatusesEnum.APPROVED) {
    return (
      <Button variant={ButtonVariantsEnum.PRIMARY} size={VisualSizesEnum.MEDIUM} isDisabled>
        <Trans id="ai-harvesting-modal-mutated-yes">Yes</Trans>
      </Button>
    );
  }

  return (
    <>
      <Button
        size={VisualSizesEnum.MEDIUM}
        onClick={() => onStatusChangeClick(ConversationAiStatusesEnum.DISCARDED)}
      >
        <Trans id="ai-harvesting-modal-not-related">Skip</Trans>
      </Button>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        size={VisualSizesEnum.MEDIUM}
        onClick={() => onStatusChangeClick(ConversationAiStatusesEnum.APPROVED)}
      >
        <Trans id="ai-harvesting-modal-is-related">Yes</Trans>
      </Button>
    </>
  );
}

function renderLoadingState() {
  return (
    <StyledWrapperDiv>
      <Skeleton width="100%" height="30px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY10} />
      <Skeleton width="100%" height="220px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY10} />
    </StyledWrapperDiv>
  );
}
